import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PaymentTab = () => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'visa',
      last4: '4242',
      expiryMonth: 12,
      expiryYear: 2027,
      isDefault: true,
      holderName: 'John Doe'
    },
    {
      id: 2,
      type: 'mastercard',
      last4: '8888',
      expiryMonth: 8,
      expiryYear: 2026,
      isDefault: false,
      holderName: 'John Doe'
    }
  ]);

  const [showAddCard, setShowAddCard] = useState(false);
  const [newCard, setNewCard] = useState({
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
    holderName: '',
    billingAddress: {
      line1: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    }
  });

  const [loading, setLoading] = useState(false);

  const handleAddCard = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Add new card to list
    const cardType = newCard?.number?.startsWith('4') ? 'visa' : 'mastercard';
    const newPaymentMethod = {
      id: paymentMethods?.length + 1,
      type: cardType,
      last4: newCard?.number?.slice(-4),
      expiryMonth: parseInt(newCard?.expiryMonth),
      expiryYear: parseInt(newCard?.expiryYear),
      isDefault: paymentMethods?.length === 0,
      holderName: newCard?.holderName
    };

    setPaymentMethods(prev => [...prev, newPaymentMethod]);
    setShowAddCard(false);
    setNewCard({
      number: '',
      expiryMonth: '',
      expiryYear: '',
      cvc: '',
      holderName: '',
      billingAddress: {
        line1: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      }
    });
    setLoading(false);
  };

  const handleSetDefault = (id) => {
    setPaymentMethods(prev => 
      prev?.map(method => ({
        ...method,
        isDefault: method?.id === id
      }))
    );
  };

  const handleDeleteCard = (id) => {
    setPaymentMethods(prev => prev?.filter(method => method?.id !== id));
  };

  const getCardIcon = (type) => {
    switch (type) {
      case 'visa':
        return 'CreditCard';
      case 'mastercard':
        return 'CreditCard';
      case 'amex':
        return 'CreditCard';
      default:
        return 'CreditCard';
    }
  };

  const getCardBrand = (type) => {
    switch (type) {
      case 'visa':
        return 'Visa';
      case 'mastercard':
        return 'Mastercard';
      case 'amex':
        return 'American Express';
      default:
        return 'Card';
    }
  };

  return (
    <div className="space-y-8">
      {/* Payment Methods */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Payment Methods</h3>
            <p className="text-sm text-muted-foreground">Manage your saved payment methods</p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddCard(true)}
          >
            Add Payment Method
          </Button>
        </div>

        <div className="space-y-4">
          {paymentMethods?.map((method) => (
            <div key={method?.id} className="border border-border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-8 bg-muted rounded flex items-center justify-center">
                  <Icon name={getCardIcon(method?.type)} size={20} />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">
                      {getCardBrand(method?.type)} •••• {method?.last4}
                    </span>
                    {method?.isDefault && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Expires {method?.expiryMonth?.toString()?.padStart(2, '0')}/{method?.expiryYear} • {method?.holderName}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {!method?.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetDefault(method?.id)}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleDeleteCard(method?.id)}
                  className="text-destructive hover:text-destructive"
                />
              </div>
            </div>
          ))}

          {paymentMethods?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h4 className="font-medium text-foreground mb-2">No payment methods</h4>
              <p className="text-sm text-muted-foreground">Add a payment method to manage your subscription</p>
            </div>
          )}
        </div>
      </div>
      {/* Add Payment Method Form */}
      {showAddCard && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Add Payment Method</h3>
              <p className="text-sm text-muted-foreground">Enter your card details securely</p>
            </div>
            <Button
              variant="ghost"
              iconName="X"
              onClick={() => setShowAddCard(false)}
            />
          </div>

          <div className="space-y-6">
            {/* Card Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Card Number"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  value={newCard?.number}
                  onChange={(e) => setNewCard(prev => ({ ...prev, number: e?.target?.value }))}
                  required
                />
              </div>

              <Input
                label="Cardholder Name"
                type="text"
                placeholder="John Doe"
                value={newCard?.holderName}
                onChange={(e) => setNewCard(prev => ({ ...prev, holderName: e?.target?.value }))}
                required
              />

              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="Month"
                  type="text"
                  placeholder="MM"
                  value={newCard?.expiryMonth}
                  onChange={(e) => setNewCard(prev => ({ ...prev, expiryMonth: e?.target?.value }))}
                  maxLength={2}
                  required
                />
                <Input
                  label="Year"
                  type="text"
                  placeholder="YYYY"
                  value={newCard?.expiryYear}
                  onChange={(e) => setNewCard(prev => ({ ...prev, expiryYear: e?.target?.value }))}
                  maxLength={4}
                  required
                />
                <Input
                  label="CVC"
                  type="text"
                  placeholder="123"
                  value={newCard?.cvc}
                  onChange={(e) => setNewCard(prev => ({ ...prev, cvc: e?.target?.value }))}
                  maxLength={4}
                  required
                />
              </div>
            </div>

            {/* Billing Address */}
            <div className="border-t border-border pt-6">
              <h4 className="font-medium text-foreground mb-4">Billing Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Input
                    label="Address Line 1"
                    type="text"
                    placeholder="123 Main Street"
                    value={newCard?.billingAddress?.line1}
                    onChange={(e) => setNewCard(prev => ({
                      ...prev,
                      billingAddress: { ...prev?.billingAddress, line1: e?.target?.value }
                    }))}
                    required
                  />
                </div>

                <Input
                  label="City"
                  type="text"
                  placeholder="New York"
                  value={newCard?.billingAddress?.city}
                  onChange={(e) => setNewCard(prev => ({
                    ...prev,
                    billingAddress: { ...prev?.billingAddress, city: e?.target?.value }
                  }))}
                  required
                />

                <Input
                  label="State"
                  type="text"
                  placeholder="NY"
                  value={newCard?.billingAddress?.state}
                  onChange={(e) => setNewCard(prev => ({
                    ...prev,
                    billingAddress: { ...prev?.billingAddress, state: e?.target?.value }
                  }))}
                  required
                />

                <Input
                  label="Postal Code"
                  type="text"
                  placeholder="10001"
                  value={newCard?.billingAddress?.postalCode}
                  onChange={(e) => setNewCard(prev => ({
                    ...prev,
                    billingAddress: { ...prev?.billingAddress, postalCode: e?.target?.value }
                  }))}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Country</label>
                  <select
                    value={newCard?.billingAddress?.country}
                    onChange={(e) => setNewCard(prev => ({
                      ...prev,
                      billingAddress: { ...prev?.billingAddress, country: e?.target?.value }
                    }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowAddCard(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                loading={loading}
                onClick={handleAddCard}
                iconName="Plus"
                iconPosition="left"
              >
                Add Payment Method
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-success mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-2">Secure Payment Processing</h4>
            <p className="text-sm text-muted-foreground">
              Your payment information is encrypted and securely processed by our PCI DSS compliant payment processor. 
              We never store your complete card details on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTab;